import type { Mesh, MeshStandardMaterial, SphereGeometry } from 'three'
import * as THREE from 'three'
import { MarkerModel } from './marker'
import { HeatmapMarkerType } from '@/common/types/elements/visualizer/marker'

// Create a cache at the module level to store materials.
const materialCache: { [severity: string]: THREE.MeshStandardMaterial } = {}

/**
 * Disposes of all cached materials to prevent memory leaks when the scene is destroyed.
 * This function should be called from VisualizerScene.destroy() method.
 */
export function disposeHeatmapMaterials() {
  for (const severity in materialCache) {
    materialCache[severity].dispose()
  }
  // Clear the cache object
  for (const prop in materialCache) {
    delete materialCache[prop]
  }
}

function temperatureToHex(severity) {
  // Clamp severity between 0 and 100
  severity = Math.max(0, Math.min(100, severity))

  // Normalize severity to a scale of 0 to 1
  const normalized = severity / 100

  // Calculate RGB values
  const red = Math.round(normalized * 255) // Red increases as severity increases
  const green = Math.round((1 - Math.abs(normalized - 0.5) * 2) * 255) // Green is highest at severity 50%
  const blue = Math.round((1 - normalized) * 255) // Blue decreases as severity increases

  // Convert RGB to hexadecimal
  const hex =
    '#' + red.toString(16).padStart(2, '0') + green.toString(16).padStart(2, '0') + blue.toString(16).padStart(2, '0')

  return hex
}

/**
 * Gets a material for a given severity, creating it only if it's not already in the cache.
 * @param severity The severity status string.
 * @returns A cached THREE.MeshStandardMaterial.
 */
function getMaterialBySeverity(severity: SeverityStatusTypes): THREE.MeshStandardMaterial {
  // 1. Check if the material for this severity is already cached.
  if (materialCache[severity]) {
    return materialCache[severity]
  }

  // 2. If not, create the new material.
  const temp = convertSeverityToTemeperature(severity)
  const colorHex = temperatureToHex(temp)
  const newMaterial = new THREE.MeshStandardMaterial({
    color: colorHex,
    roughness: 0.5,
    metalness: 0.1,
    opacity: 0.5,
    transparent: true,
  })

  // 3. Store it in the cache and then return it.
  materialCache[severity] = newMaterial
  return newMaterial
}

const severityTemps = {
  requires_inspection: 80,
  grounding_condition: 100,
  non_grounding_condition: 90,
  for_awareness: 50,
  suggestion: 30,
}

enum SeverityStatusTypes {
  requires_inspection = 'Requires Inspection',
  grounding_condition = 'Grounding Condition',
  non_grounding_condition = 'Non Grounding Condition',
  for_awareness = 'For Awareness',
  suggestion = 'Suggestion',
}

function convertSeverityToTemeperature(severity: SeverityStatusTypes): number {
  const key = Object.entries(SeverityStatusTypes).find(([key, val]) => val === severity)?.[0]

  return severityTemps[key] ? severityTemps[key] : severityTemps.suggestion
}

interface HeatmapMarkerModel extends MarkerModel {
  marker: HeatmapMarkerType
}

class HeatmapMarkerModel extends MarkerModel {
  constructor(marker, eventCallback?, interactionManager?, mesh?) {
    super(marker, eventCallback, interactionManager, mesh)
    // Now this uses the efficient, cached version
    const defaultMaterial = getMaterialBySeverity(marker.severity)
    this.defaultMaterial = defaultMaterial
  }

  /**
   * Creates Marker mesh and sets default properties for the mesh
   */
  createMarkerMesh(): Mesh<SphereGeometry, MeshStandardMaterial> | null {
    if (!this.marker) {
      console.error('No marker information was provided.')
      return null
    }

    const markerGeometry = new THREE.SphereGeometry(1, 12, 8)
    const markerMesh = new THREE.Mesh(markerGeometry, this.defaultMaterial)
    const markerPosition = new THREE.Vector3(this.marker.locx, this.marker.locy, this.marker.locz)

    markerMesh.position.set(markerPosition.x, markerPosition.y, markerPosition.z)
    markerMesh.name = this.marker.name

    return markerMesh
  }

  setMaterial() {
    if (this.mesh) {
      this.mesh.material = this.defaultMaterial
    }
  }
}

export { HeatmapMarkerModel }
